type PaperlessStatus = "accepted" | "rejected";

export interface PaperlessOutcome {
  acceptedPaperlessDocument?: boolean | null;
  courierAlertCode?: string | null;
  courierAlertDescription?: string | null;
}

export interface PaperlessRejection {
  message: string;
  code: string | null;
}

/** Null when the courier reported no outcome: the list shows no icon. */
export function paperlessStatus(
  shipment: PaperlessOutcome
): PaperlessStatus | null {
  if (shipment.acceptedPaperlessDocument === true) return "accepted";
  if (shipment.acceptedPaperlessDocument === false) return "rejected";
  return null;
}

/**
 * Why the courier refused: the description is what the user reads and the code
 * is the reference beside it. With no description the code becomes the message,
 * because it is all the courier sent. Null when it sent neither: the caller
 * shows the default label.
 */
export function paperlessRejection(
  shipment: PaperlessOutcome
): PaperlessRejection | null {
  const code = filled(shipment.courierAlertCode);
  const description = filled(shipment.courierAlertDescription);
  if (description) return { message: description, code };
  if (code) return { message: code, code: null };
  return null;
}

/**
 * The same rejection as the user reads it. The translator is a parameter and
 * not a dependency so this stays a rule and not a screen: the courier can
 * repeat itself, and two different codes can resolve to the same sentence, so
 * the two halves are compared once translated. With nothing to say the message
 * is the default label.
 */
export function paperlessRejectionParts(
  shipment: PaperlessOutcome,
  translate: (value: string) => string
): PaperlessRejection {
  const rejection = paperlessRejection(shipment);
  if (!rejection) return { message: translate("DOCUMENT_MISSING"), code: null };
  const message = translate(rejection.message);
  const code = rejection.code ? translate(rejection.code) : null;
  return { message, code: code === message ? null : code };
}

function filled(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}
